import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { User, UserRole } from '../types/User';
import { initializeTestAccounts } from '../utils/initializeAccounts';
import authService from '../services/authService';

interface AuthContextType {
  user: User | null;
  login: (centerId: string, email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  signup: (email: string, password: string, name: string, role?: UserRole) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  hasPermission: (allowedRoles: UserRole[]) => boolean;
  centerInfo: any;
  selectedCenterId: string | null;
  setSelectedCenterId: (centerId: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  
  // const [centerInfo, setCenterInfo] = useState<any>(() => {
  //   const savedCenterId = localStorage.getItem('centerId');
  //   if (savedCenterId) {
  //     return authService.getCenterInfo(savedCenterId);
  //   }
  //   return null;
  // });

  // System Admin이 선택한 센터 ID를 관리
  const [selectedCenterId, setSelectedCenterIdState] = useState<string | null>(() => {
    const savedSelectedCenterId = localStorage.getItem('selectedCenterId');
    return savedSelectedCenterId || null;
  });

  useEffect(() => {
    // Initialize test accounts on component mount
    initializeTestAccounts();
  }, []);

  const login = async (centerId: string, loginId: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const result = await authService.loginWithCenter({
        centerId,
        loginId,
        password
      });

      if (result.success && result.user) {
        setUser(result.user);
        //setCenterInfo(result.user.centerInfo);
        localStorage.setItem('user', JSON.stringify(result.user));
        if (result.token) {
          localStorage.setItem('token', result.token);
        }
        return { success: true };
      }

      return { 
        success: false, 
        message: result.message || '로그인에 실패했습니다.' 
      };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: '로그인 중 오류가 발생했습니다.' 
      };
    }
  };

  const signup = async (email: string, password: string, name: string, role: UserRole = UserRole.GENERAL_USER): Promise<boolean> => {
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    const existingUser = users.find((u: any) => u.email === email);
    
    if (existingUser) {
      return false;
    }

    const newUser = {
      id: Date.now().toString(),
      email,
      password,
      name,
      role
    };

    users.push(newUser);
    localStorage.setItem('users', JSON.stringify(users));

    const userData: User = {
      id: newUser.id,
      email: newUser.email,
      name: newUser.name,
      role: newUser.role
    };
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    
    return true;
  };

  const logout = () => {
    setUser(null);
    //setCenterInfo(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('centerId');
    localStorage.removeItem('serverGroup');
    localStorage.removeItem('serverUrl');
  };

  const hasPermission = (allowedRoles: UserRole[]): boolean => {
    if (!user) return false;
    return allowedRoles.includes(user.role);
  };

  // selectedCenterId 설정 함수
  const setSelectedCenterId = (centerId: string | null) => {
    setSelectedCenterIdState(centerId);
    if (centerId) {
      localStorage.setItem('selectedCenterId', centerId);
      // 센터 변경 이벤트 발생
      window.dispatchEvent(new CustomEvent('centerChanged', { detail: { centerId } }));
    } else {
      localStorage.removeItem('selectedCenterId');
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      signup,
      logout,
      isAuthenticated: !!user,
      hasPermission,
      centerInfo: null,
      selectedCenterId,
      setSelectedCenterId
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};